import { inject } from '@angular/core';
import { CanMatchFn, Route, UrlSegment, Router, UrlTree } from '@angular/router';
import { RootScopeService } from '../services/root-scope.service';

export const featureFlagCanMatch: CanMatchFn = (
  route: Route,
  _segments: UrlSegment[]
): boolean | UrlTree | Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const root = inject(RootScopeService);

  const flagKey = route.data?.['flag'] as string | undefined;
  if (!flagKey) return true; // No flag requirement

  const ready = root.launchDarklyReady?.() ?? false;
  
  // If LaunchDarkly is ready, check immediately
  if (ready) {
    const enabled = isFlagEnabled(flagKey, root);
    if (enabled === true) {
      return true;
    } else {
      console.log(` Access denied for ${flagKey} - redirecting to home`);
      return router.parseUrl('/home');
    }
  }

  // If not ready, wait for it with timeout
  return new Promise<boolean | UrlTree>((resolve) => {
    const maxWait = 10000; // 10 seconds
    const startTime = Date.now();
    
    const checkReady = () => {
      const isReady = root.launchDarklyReady?.() ?? false;
      
      if (isReady) {
        const enabled = isFlagEnabled(flagKey, root);
        if (enabled === true) {
          console.log(` Access granted for ${flagKey}`);
          resolve(true);
        } else {
          console.log(` Access denied for ${flagKey} - redirecting to home`);
          resolve(router.parseUrl('/home'));
        }
      } else {
        const elapsed = Date.now() - startTime;
        if (elapsed >= maxWait) {
          console.warn(` LaunchDarkly timeout for ${flagKey} - denying access for security`);
          resolve(router.parseUrl('/home'));
        } else {
          setTimeout(checkReady, 200); // Check every 200ms
        }
      }
    };
    
    checkReady();
  });
};

function isFlagEnabled(flagKey: string, root: RootScopeService): boolean | undefined {
  // Return raw signal value (can be true | false | undefined) so guard can distinguish 'not yet loaded'.
  switch (flagKey) {
    case 'CBEM-visible': return root.cbemVisible();
    case 'master-client-visible': return root.masterClientVisible();
    case 'invoice-extract-visible': return root.invoiceExtractVisible();
    case 'gb-referrals-visible': return root.referralVisible();
    case 'utility-visible': return root.dynamoUtilityVisible();
    default: return false; // unknown flag → treat as disabled
  }
}